import qrcode from 'qrcode-generator';
import { useMemo } from 'react';

// Encodes wifi credentials as the standard WIFI: URI. Scanning with an iOS
// or Android camera prompts "Join <SSID>" — no typing required.
// Format: WIFI:T:<auth>;S:<ssid>;P:<password>;H:<hidden>;;
// Special characters in SSID/password must be backslash-escaped.

function escapeWifi(value: string): string {
  return value.replace(/([\\;,":])/g, '\\$1');
}

export interface WifiQRProps {
  ssid: string;
  password: string;
  auth?: 'WPA' | 'WEP' | 'nopass';
  hidden?: boolean;
  size?: number;
}

export function WifiQR({ ssid, password, auth = 'WPA', hidden = false, size = 220 }: WifiQRProps) {
  const svg = useMemo(() => {
    const payload =
      auth === 'nopass'
        ? `WIFI:T:nopass;S:${escapeWifi(ssid)};;`
        : `WIFI:T:${auth};S:${escapeWifi(ssid)};P:${escapeWifi(password)};${hidden ? 'H:true;' : ''};`;

    // typeNumber=0 lets the library pick the smallest QR version that fits;
    // 'M' is medium error correction (good balance for print/scan reliability).
    const qr = qrcode(0, 'M');
    qr.addData(payload);
    qr.make();
    // Generate SVG string directly (no <img>, no base64 round-trip).
    return qr.createSvgTag({ cellSize: 4, margin: 4, scalable: true });
  }, [ssid, password, auth, hidden]);

  return (
    <div
      aria-label={`Wi-Fi QR code for network ${ssid}`}
      style={{
        width: size,
        height: size,
        background: '#fff',
        borderRadius: 12,
        padding: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      // The generated SVG is trusted (produced locally from static input);
      // rendering as innerHTML lets it inherit the container's sizing.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
